/**
 * Board Status Filter Utility
 * Handles filtering job statuses that start with "invoice" and manages local storage for performance
 */

const STORAGE_KEY = 'board_invoice_exclude_cache';
const CACHE_EXPIRY_KEY = 'board_invoice_exclude_cache_expiry';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export interface StatusItem {
  id: number;
  status?: string;
  name?: string;
}

export interface ExcludeCache {
  invoiceStatusIds: number[];
  lastUpdated: number;
}

/**
 * Get invoice status IDs to exclude from board queries
 * Uses local storage for caching with expiry
 */
export async function getInvoiceExcludeStatusIds(
  globalService: any,
  MODULE: any,
  forceRefresh: boolean = false
): Promise<number[]> {
  // Check if we should force refresh (refresh button clicked)
  if (forceRefresh) {
    clearInvoiceExcludeCache();
  }

  // Try to get from cache first
  const cached = getCachedInvoiceExcludes();
  if (cached && !forceRefresh) {
    return cached.invoiceStatusIds;
  }

  try {
    // Fetch all job statuses from API
    const statusOptions = await globalService.getUXDropDown(MODULE.PROJECTS.Status);
    
    // Filter statuses that contain "invoice" (case insensitive)
    const invoiceStatusIds = statusOptions
      .filter((status: StatusItem) => {
        const statusName = status.status || status.name || '';
        return statusName.toLowerCase().includes('invoice');
      })
      .map((status: StatusItem) => status.id);

    // Cache the results
    setCachedInvoiceExcludes(invoiceStatusIds);

    return invoiceStatusIds;
  } catch (error) {
    // If API call fails, return cached data if available, otherwise empty array
    const cached = getCachedInvoiceExcludes();
    return cached ? cached.invoiceStatusIds : [];
  }
}

/**
 * Build the exclude parameter string for API queries
 * Combines existing excludes with invoice status IDs
 */
export function buildExcludeString(
  existingExclude: string | undefined,
  invoiceStatusIds: number[]
): string {
  const excludeIds: number[] = [];

  // Parse existing exclude string
  if (existingExclude) {
    const existingIds = existingExclude.split(',')
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id));
    excludeIds.push(...existingIds);
  }

  // Add invoice status IDs
  excludeIds.push(...invoiceStatusIds);

  // Remove duplicates and return as comma-separated string
  const uniqueIds = excludeIds.filter((id, index) => excludeIds.indexOf(id) === index);
  return uniqueIds.join(',');
}

/**
 * Get cached invoice exclude data
 */
function getCachedInvoiceExcludes(): ExcludeCache | null {
  try {
    const cachedData = localStorage.getItem(STORAGE_KEY);
    const cacheExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);

    if (!cachedData || !cacheExpiry) {
      return null;
    }

    const expiry = parseInt(cacheExpiry);
    if (Date.now() > expiry) {
      // Cache expired, clear it
      clearInvoiceExcludeCache();
      return null;
    }

    return JSON.parse(cachedData);
  } catch (error) {
    // If there's an error parsing cache, clear it
    clearInvoiceExcludeCache();
    return null;
  }
}

/**
 * Cache invoice exclude data
 */
function setCachedInvoiceExcludes(invoiceStatusIds: number[]): void {
  try {
    const cacheData: ExcludeCache = {
      invoiceStatusIds,
      lastUpdated: Date.now()
    };

    const expiry = Date.now() + CACHE_DURATION;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
    localStorage.setItem(CACHE_EXPIRY_KEY, expiry.toString());
  } catch (error) {
    // Silently fail if localStorage is not available
  }
}

/**
 * Clear cached invoice exclude data
 */
export function clearInvoiceExcludeCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CACHE_EXPIRY_KEY);
  } catch (error) {
    // Silently fail if localStorage is not available
  }
}

/**
 * Check if cache exists and is valid
 */
export function hasValidCache(): boolean {
  const cached = getCachedInvoiceExcludes();
  return cached !== null;
}
